import { html } from '@microsoft/fast-element';
import {
  formatDateLong,
  formatCodDate,
  getFormattedNumber,
  getPriceValue,
} from '../../../utils/formatting';
import { getAbbreviationForCreditProfile } from '../../../utils/credit-profiles';
import type { VppaDetailModal } from './vppa-detail-modal';
import { CASCADE_ENUM } from '../../../services/enum.service';

export const VppaDetailModalTemplate = html<VppaDetailModal>`
  <right-side-modal
    classes="user-modal"
    :show="${(x) => x.show}"
    allowOverlayClose="true"
    @modalClosing=${(x) => x.closeModal()}
    width="${(x) => x.width}"
    header="${(x) => x.title}"
    buttons="false"
    closeModalHeader="${(x) => x.closeModal()}"
    closeModalDescription="${(x) => x.closeModal()}"
  >
    <div class="vppa-modal-content">
      <div class="vppa-modal-details">
        ${(x) => `Last Updated: ${formatCodDate(x.data.MODIFIED_AT)}`}
      </div>
      <div class="vppa-modal-row">
        <span>ISO</span>
        <span>${(x) => x.data.ISO}</span>
      </div>
      <div class="vppa-modal-row">
        <span>Contract ID</span>
        <span>${(x) => x.data.UNIQUE_ID}</span>
      </div>
      <div class="vppa-modal-row">
        <span>Contract Symbol</span>
        <span>${(x) => x.data.TICKER}</span>
      </div>
      <div class="vppa-modal-row">
        <span>Project Name</span>
        <span>${(x) => x.data.PROJECT_NAME}</span>
      </div>
      <div class="vppa-modal-row">
        <span>Technology Type</span>
        <span>
          ${(x) => x.enumService.getEnumTranslation(CASCADE_ENUM.TECHNOLOGY, x.data.TECHNOLOGY)}
        </span>
      </div>
      <div class="vppa-modal-row">
        <span>Contract Type</span>
        <span>
          ${(x) =>
            x.enumService.getEnumTranslation(CASCADE_ENUM.CONTRACT_TYPE, x.data.CONTRACT_TYPE)}
        </span>
      </div>
      <div class="vppa-modal-row">
        <span>Target COD</span>
        <span>${(x) => formatCodDate(x.data.TARGET_COD)}</span>
      </div>
      <div class="vppa-modal-row">
        <span>Guaranteed COD</span>
        <span>${(x) => formatCodDate(x.data.GUARANTEED_COD)}</span>
      </div>
      <div class="vppa-modal-row">
        <span>Nameplate Capacity</span>
        <span>${(x) => x.data.MEGAWATT_AC}</span>
      </div>
      <div class="vppa-modal-row">
        <span>Offered Capacity</span>
        <span>${(x) => x.data.OFFERED_CAPACITY}</span>
      </div>
      <div class="vppa-modal-row">
        <span>Buyer's Fraction</span>
        <span>${(x) => getFormattedNumber(x.data.BUYERS_FRACTION)}</span>
      </div>
      <div class="vppa-modal-row">
        <span>Tenor Start Date</span>
        <span>${(x) => formatDateLong(x.data.TENOR_START_DATE as number)}</span>
      </div>
      <div class="vppa-modal-row">
        <span>Tenor (Months)</span>
        <span>${(x) => getFormattedNumber(x.data.TENOR_MONTHS)}</span>
      </div>
      <div class="vppa-modal-row">
        <span>Seller</span>
        <span>${(x) => x.data.COMPANY_NAME}</span>
      </div>
      <div class="vppa-modal-row">
        <span>Settlement Point</span>
        <span>${(x) => x.data.SETTLEMENT_POINT}</span>
      </div>
      <div class="vppa-modal-row">
        <span>Settlement Point Index</span>
        <span>
          ${(x) =>
            x.enumService.getEnumTranslation(
              CASCADE_ENUM.SETTLEMENT_INDEX,
              x.data.SETTLEMENT_INDEX,
            )}
        </span>
      </div>
      <div class="vppa-modal-row">
        <span>Fixed Price</span>
        <span>${(x) => getPriceValue(getFormattedNumber(x.data.FIXED_PRICE), '')}</span>
      </div>
      <div class="vppa-modal-row">
        <span>Interconnection Point</span>
        <span>${(x) => x.data.INTERCONNECTION_POINT}</span>
      </div>
      <div class="vppa-modal-row">
        <span>Credit Profile</span>
        <span>
          ${(x) =>
            x.enumService.getEnumTranslation(
              CASCADE_ENUM.VPPA_CREDIT_PROFILE,
              x.data.VPPA_CREDIT_PROFILE,
            )}
        </span>
      </div>
      <div class="vppa-modal-row">
        <span>Seller Post-COD Credit ($/MW) Max</span>
        <span>
          ${(x) => {
            if (x.data.SELLER_POST_COD_COLLATERAL && x.data.SELLER_POST_COD_COLLATERAL > 0) {
              return getPriceValue(
                getFormattedNumber(x.data.SELLER_POST_COD_COLLATERAL),
                getAbbreviationForCreditProfile(x.data.VPPA_CREDIT_PROFILE),
              );
            }
            return getAbbreviationForCreditProfile(x.data.VPPA_CREDIT_PROFILE);
          }}
        </span>
      </div>
      <div class="vppa-modal-row">
        <span>Negative Pricing Method</span>
        <span>
          ${(x) =>
            x.enumService.getEnumTranslation(
              CASCADE_ENUM.NEGATIVE_PRICING_METHOD,
              x.data.NEGATIVE_PRICING_METHOD,
            )}
        </span>
      </div>
      <div class="vppa-modal-row">
        <span>Buyer Upside Allocation</span>
        <span>
          ${(x) =>
            x.enumService.getEnumTranslation(
              CASCADE_ENUM.BUYER_UPSIDE_ALLOCATION,
              x.data.BUYER_UPSIDE_ALLOCATION,
            )}
        </span>
      </div>
      <div class="vppa-modal-row">
        <span>Applicable REC Program</span>
        <span>
          ${(x) =>
            x.enumService.getEnumTranslation(
              CASCADE_ENUM.APPLICABLE_REC_PROGRAM,
              x.data.APPLICABLE_REC_PROGRAM,
            )}
        </span>
      </div>
      <div class="vppa-modal-row">
        <span>Applicable Tracking System</span>
        <span>
          ${(x) =>
            x.enumService.getEnumTranslation(
              CASCADE_ENUM.APPLICABLE_TRACKING_SYSTEM,
              x.data.APPLICABLE_TRACKING_SYSTEM,
            )}
        </span>
      </div>
      <div class="vppa-modal-row">
        <span>REC Delivery Methodology</span>
        <span>
          ${(x) =>
            x.enumService.getEnumTranslation(
              CASCADE_ENUM.REC_DELIVERY_METHODOLOGY,
              x.data.REC_DELIVERY_METHODOLOGY,
            )}
        </span>
      </div>
      <div class="vppa-modal-row">
        <span>Performance Guarantee Method</span>
        <span>
          ${(x) =>
            x.enumService.getEnumTranslation(
              CASCADE_ENUM.PERFORMANCE_GUARANTEE_METHOD,
              x.data.PERFORMANCE_GUARANTEE_METHOD,
            )}
        </span>
      </div>
    </div>
  </right-side-modal>
`;
